// The drag semantics of the job file area, kept away from the components so
// they can be reasoned about — and tested — without a drag actually happening.

const FOLDER_PREFIX = 'folder:';
const ROOT_ID = `${FOLDER_PREFIX}root`;

/** The droppable id for a folder, or for the job root. */
export function folderDropId(folderId: string | null): string {
  return folderId === null ? ROOT_ID : `${FOLDER_PREFIX}${folderId}`;
}

/**
 * The folder a droppable id names.
 *
 * `null` is the job root and `undefined` is "not a folder at all" — two
 * different answers, which is why this doesn't return a plain `string | null`.
 */
export function folderIdFromDropId(dropId: string): string | null | undefined {
  if (dropId === ROOT_ID) return null;
  if (dropId.startsWith(FOLDER_PREFIX)) return dropId.slice(FOLDER_PREFIX.length);
  return undefined;
}

export interface DropResolution {
  /** The files to move, in selection order. */
  ids: string[];
  /** Where they are going; null is the job root. */
  folderId: string | null;
}

interface ResolveDropInput {
  activeId: string;
  /** The droppable under the pointer at drop time, or null for none. */
  overId: string | null;
  selectedIds: ReadonlySet<string>;
  /** The folder each visible file currently sits in. */
  fileFolderIds: ReadonlyMap<string, string | null>;
}

/**
 * What a drop moves, and where — or null when it moves nothing.
 *
 * Dragging a file that is part of the selection takes the whole selection with
 * it; dragging one from outside the selection takes only that file. Files
 * already sitting in the target are dropped from the batch rather than sent to
 * the server to be told they are already there, and when that empties the batch
 * the drop is a no-op.
 */
export function resolveDrop({
  activeId,
  overId,
  selectedIds,
  fileFolderIds,
}: ResolveDropInput): DropResolution | null {
  if (!overId) return null;

  const folderId = folderIdFromDropId(overId);
  if (folderId === undefined) return null;

  const dragged = selectedIds.has(activeId) ? [...selectedIds] : [activeId];
  const ids = dragged.filter((id) => fileFolderIds.get(id) !== folderId);

  return ids.length > 0 ? { ids, folderId } : null;
}
